import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Footer from '../../components/ui/Footer';
import SearchBar from './components/SearchBar';
import FilterPanel from './components/FilterPanel';
import SortControls from './components/SortControls';
import InstitutionCard from './components/InstitutionCard';
import EmptyState from './components/EmptyState';
import LoadingState from './components/LoadingState';
import ScheduleConsultationModal from '../../components/ui/ScheduleConsultationModal';

const InstitutionSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedProgramType, setSelectedProgramType] = useState('all');
  const [selectedDuration, setSelectedDuration] = useState('all');
  const [selectedDeadline, setSelectedDeadline] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredInstitutions, setFilteredInstitutions] = useState([]);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);

  const mockInstitutions = [
  {
    id: 1,
    name: "Peking University",
    location: "Beijing",
    image: "https://images.unsplash.com/photo-1697395710104-e983bbfae7d6",
    imageAlt: "Historic red traditional Chinese architecture building with ornate roof details at Peking University campus in Beijing",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1b2cf6922-1766559041924.png",
    logoAlt: "Peking University official emblem featuring traditional Chinese design elements in red and gold colors",
    programType: "Language Programs & Preparatory",
    duration: "6-12 months",
    studentCount: "2,500+",
    deadline: "March 31, 2026",
    highlights: ["HSK Preparation", "Cultural Immersion", "Scholarship Available"],
    featured: true,
    accredited: true
  },
  {
    id: 2,
    name: "Tsinghua University",
    location: "Beijing",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1573444f3-1767611744232.png",
    imageAlt: "Modern academic building with glass facade and contemporary architecture at Tsinghua University campus surrounded by green landscaping",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_19957e3d1-1766559040890.png",
    logoAlt: "Tsinghua University circular logo with purple and white color scheme featuring university name in Chinese characters",
    programType: "Intensive Language Courses",
    duration: "3-6 months",
    studentCount: "1,800+",
    deadline: "April 15, 2026",
    highlights: ["Business Chinese", "Tech Focus", "Industry Partnerships"],
    featured: true,
    accredited: true
  },
  {
    id: 3,
    name: "Fudan University",
    location: "Shanghai",
    image: "https://images.unsplash.com/photo-1730499805576-924c9b0ed229",
    imageAlt: "Classical European-style university building with columns and arched windows at Fudan University Shanghai campus with students walking",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_174d31a05-1768718565271.png",
    logoAlt: "Fudan University shield logo in blue and white featuring Chinese characters and established date",
    programType: "Language Programs",
    duration: "6-12 months",
    studentCount: "2,200+",
    deadline: "March 20, 2026",
    highlights: ["Small Class Sizes", "City Center Location", "Cultural Activities"],
    featured: false,
    accredited: true
  },
  {
    id: 4,
    name: "Shanghai Jiao Tong University",
    location: "Shanghai",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_139a170a9-1765430406255.png",
    imageAlt: "Modern university campus with tall academic buildings and wide plaza area at Shanghai Jiao Tong University with blue sky background",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_13c700577-1768718569772.png",
    logoAlt: "Shanghai Jiao Tong University circular emblem with red and white colors featuring anchor symbol and Chinese text",
    programType: "Preparatory Classes",
    duration: "12+ months",
    studentCount: "1,500+",
    deadline: "April 30, 2026",
    highlights: ["Engineering Focus", "Research Opportunities", "Modern Facilities"],
    featured: false,
    accredited: true
  },
  {
    id: 5,
    name: "Zhejiang University",
    location: "Hangzhou",
    image: "https://images.unsplash.com/photo-1697205773582-206fa20cb8e1",
    imageAlt: "Scenic university campus with traditional Chinese pavilion beside lake with willow trees and mountain backdrop at Zhejiang University Hangzhou",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7c4b682-1768718566119.png",
    logoAlt: "Zhejiang University logo featuring blue shield design with Chinese characters and university founding year",
    programType: "Language Programs",
    duration: "6-12 months",
    studentCount: "1,900+",
    deadline: "March 25, 2026",
    highlights: ["Scenic Campus", "Tech Hub Location", "Innovation Programs"],
    featured: true,
    accredited: true
  },
  {
    id: 6,
    name: "Nanjing University",
    location: "Nanjing",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_183e91189-1768045419363.png",
    imageAlt: "Historic brick university building with clock tower and traditional architecture at Nanjing University campus with autumn trees",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_17024cbbd-1768718566832.png",
    logoAlt: "Nanjing University circular seal with purple color scheme featuring Chinese characters and university emblem",
    programType: "Intensive Language Courses",
    duration: "3-6 months",
    studentCount: "1,400+",
    deadline: "April 10, 2026",
    highlights: ["Historic City", "Cultural Heritage", "Affordable Tuition"],
    featured: false,
    accredited: true
  },
  {
    id: 7,
    name: "Sun Yat-sen University",
    location: "Guangzhou",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1aa274fd4-1768226613173.png",
    imageAlt: "Tropical university campus with palm trees and modern academic buildings at Sun Yat-sen University in Guangzhou southern China",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_111a17b26-1768718567969.png",
    logoAlt: "Sun Yat-sen University logo with green and white colors featuring university name in Chinese and English",
    programType: "Language Programs & Preparatory",
    duration: "6-12 months",
    studentCount: "2,000+",
    deadline: "March 28, 2026",
    highlights: ["Southern Culture", "Cantonese Option", "International Community"],
    featured: false,
    accredited: true
  },
  {
    id: 8,
    name: "Xi\'an Jiaotong University",
    location: "Xi\'an",
    image: "https://images.unsplash.com/photo-1715419796163-0a877a61f35a",
    imageAlt: "Ancient city university campus with traditional Chinese architecture and modern facilities at Xi\'an Jiaotong University with historic city walls visible",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1fc592941-1768718577012.png",
    logoAlt: "Xi\'an Jiaotong University shield emblem with red and gold colors featuring Chinese characters and university seal",
    programType: "Preparatory Classes",
    duration: "12+ months",
    studentCount: "1,600+",
    deadline: "April 20, 2026",
    highlights: ["Ancient Capital", "Silk Road History", "Lower Cost of Living"],
    featured: false,
    accredited: true
  },
  {
    id: 9,
    name: "Sichuan University",
    location: "Chengdu",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e564c4f3-1767944348988.png",
    imageAlt: "University campus with bamboo gardens and traditional Chinese architecture at Sichuan University in Chengdu with mountain views",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef585986-1768718567318.png",
    logoAlt: "Sichuan University circular logo with red color scheme featuring panda symbol and Chinese university name",
    programType: "Language Programs",
    duration: "6-12 months",
    studentCount: "1,700+",
    deadline: "March 15, 2026",
    highlights: ["Panda Research Base", "Sichuan Cuisine", "Relaxed Lifestyle"],
    featured: true,
    accredited: true
  },
  {
    id: 10,
    name: "Harbin Institute of Technology",
    location: "Harbin",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e0e93e64-1768718567437.png",
    imageAlt: "Winter campus scene with snow-covered buildings and Russian-influenced architecture at Harbin Institute of Technology in northern China",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11e872913-1768718566613.png",
    logoAlt: "Harbin Institute of Technology logo with blue and white colors featuring Chinese characters and engineering symbols",
    programType: "Intensive Language Courses",
    duration: "3-6 months",
    studentCount: "1,300+",
    deadline: "April 5, 2026",
    highlights: ["Winter Experience", "Russian Influence", "Engineering Excellence"],
    featured: false,
    accredited: true
  },
  {
    id: 11,
    name: "Wuhan University",
    location: "Wuhan",
    image: "https://images.unsplash.com/photo-1680134901342-a659e61a63b7",
    imageAlt: "Cherry blossom lined pathways and traditional Chinese buildings at Wuhan University campus during spring season with pink flowers",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c6f45d90-1768718567424.png",
    logoAlt: "Wuhan University emblem with blue color scheme featuring Chinese characters and university founding date",
    programType: "Language Programs",
    duration: "6-12 months",
    studentCount: "1,800+",
    deadline: "March 22, 2026",
    highlights: ["Cherry Blossoms", "Central Location", "Comprehensive Programs"],
    featured: false,
    accredited: true
  },
  {
    id: 12,
    name: "Tianjin University",
    location: "Tianjin",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19bf7aa13-1764782659695.png",
    imageAlt: "Modern university campus with contemporary architecture and green spaces at Tianjin University near Beijing with waterfront views",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e7757cda-1768718567023.png",
    logoAlt: "Tianjin University circular seal with purple and white colors featuring Chinese characters and university emblem",
    programType: "Business Chinese",
    duration: "3-6 months",
    studentCount: "1,200+",
    deadline: "April 12, 2026",
    highlights: ["Business Focus", "Near Beijing", "Port City Culture"],
    featured: false,
    accredited: true
  }];


  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
      setFilteredInstitutions(mockInstitutions);
    }, 1500);
  }, []);

  useEffect(() => {
    let results = [...mockInstitutions];

    if (searchQuery) {
      results = results?.filter(
        (inst) =>
        inst?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        inst?.location?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        inst?.programType?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (selectedLocation !== 'all') {
      results = results?.filter(
        (inst) => inst?.location?.toLowerCase() === selectedLocation?.toLowerCase()
      );
    }

    if (selectedProgramType !== 'all') {
      results = results?.filter((inst) =>
      inst?.programType?.toLowerCase()?.includes(selectedProgramType?.toLowerCase())
      );
    }

    if (selectedDuration !== 'all') {
      results = results?.filter((inst) => {
        const duration = inst?.duration?.toLowerCase();
        if (selectedDuration === '1-3') return duration?.includes('1-3') || duration?.includes('3-6');
        if (selectedDuration === '3-6') return duration?.includes('3-6');
        if (selectedDuration === '6-12') return duration?.includes('6-12');
        if (selectedDuration === '12+') return duration?.includes('12+');
        return true;
      });
    }

    switch (sortBy) {
      case 'name-asc':
        results?.sort((a, b) => a?.name?.localeCompare(b?.name));
        break;
      case 'name-desc':
        results?.sort((a, b) => b?.name?.localeCompare(a?.name));
        break;
      case 'students-desc':
        results?.sort((a, b) => {
          const aCount = parseInt(a?.studentCount?.replace(/[^0-9]/g, ''));
          const bCount = parseInt(b?.studentCount?.replace(/[^0-9]/g, ''));
          return bCount - aCount;
        });
        break;
      case 'deadline-asc':
        results?.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
        break;
      case 'deadline-desc':
        results?.sort((a, b) => new Date(b.deadline) - new Date(a.deadline));
        break;
      default:
        results?.sort((a, b) => (b?.featured ? 1 : 0) - (a?.featured ? 1 : 0));
    }

    setFilteredInstitutions(results);
  }, [searchQuery, selectedLocation, selectedProgramType, selectedDuration, selectedDeadline, sortBy]);

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedLocation('all');
    setSelectedProgramType('all');
    setSelectedDuration('all');
    setSelectedDeadline('all');
    setSortBy('relevance');
  };

  const activeFilterCount = [
  selectedLocation !== 'all',
  selectedProgramType !== 'all',
  selectedDuration !== 'all',
  selectedDeadline !== 'all']?.
  filter(Boolean)?.length;

  const hasActiveFilters = activeFilterCount > 0 || searchQuery?.length > 0;

  return (
    <>
      <Helmet>
        <title>Find Chinese Language Programs | ChineseLangApp</title>
        <meta
          name="description"
          content="Search and compare Chinese language programs and preparatory classes at top universities across China. Find the perfect program for your educational goals." />

      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <div className="pt-20">
          <Breadcrumbs />
        </div>

        <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-3">
              Find Your Perfect Program
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-measure">
              Discover Chinese language programs and preparatory classes at leading universities across China. Filter by location, program type, and more to find your ideal match.
            </p>
          </div>

          <div className="mb-6">
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onClearSearch={() => setSearchQuery('')} />

          </div>

          <div className="mb-6 md:hidden">
            <Button
              variant="outline"
              fullWidth
              iconName="Filter"
              iconPosition="left"
              onClick={() => setIsMobileFilterOpen(true)}>

              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </Button>
          </div>

          <FilterPanel
            selectedLocation={selectedLocation}
            onLocationChange={setSelectedLocation}
            selectedProgramType={selectedProgramType}
            onProgramTypeChange={setSelectedProgramType}
            selectedDuration={selectedDuration}
            onDurationChange={setSelectedDuration}
            selectedDeadline={selectedDeadline}
            onDeadlineChange={setSelectedDeadline}
            onResetFilters={handleResetFilters}
            activeFilterCount={activeFilterCount}
            isMobileFilterOpen={isMobileFilterOpen}
            onCloseMobileFilter={() => setIsMobileFilterOpen(false)} />


          <div className="mt-6">
            <SortControls
              sortBy={sortBy}
              onSortChange={setSortBy}
              resultCount={filteredInstitutions?.length} />

          </div>

          <div className="mt-6">
            {isLoading ?
            <LoadingState /> :
            filteredInstitutions?.length > 0 ?
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredInstitutions?.map((institution) =>
              <InstitutionCard key={institution?.id} institution={institution} />
              )}
              </div> :

            <EmptyState
              onResetFilters={handleResetFilters}
              hasActiveFilters={hasActiveFilters} />

            }
          </div>

          {filteredInstitutions?.length > 0 && !isLoading &&
          <div className="mt-8 bg-primary/5 rounded-lg p-6 md:p-8 border border-primary/20">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Info" size={24} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                    Need Help Choosing?
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Our education advisors can help you find the perfect program based on your goals, budget, and preferences. Schedule a free consultation today.
                  </p>
                  <Button 
                    variant="default" 
                    iconName="Calendar" 
                    iconPosition="left"
                    onClick={() => setIsConsultationModalOpen(true)}
                  >
                    Schedule Consultation
                  </Button>
                </div>
              </div>
            </div>
          }
        </main>
        
        <Footer />

        <ScheduleConsultationModal
          isOpen={isConsultationModalOpen}
          onClose={() => setIsConsultationModalOpen(false)}
          instructorName="Academic Advisor"
        />
      </div>
    </>);

};

export default InstitutionSearch;